import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import { SpinnerGap, CheckCircle, Trophy, ArrowUp, ArrowDown, Minus } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fINR = (a) => `₹${(a || 0).toLocaleString('en-IN')}`;

const QuarterlyBonusSummary = () => {
  const { user, isSuperadmin, isCeo } = useAuth();
  const isAdmin = isSuperadmin || isCeo;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(null);

  const now = new Date();
  const qNum = Math.floor(now.getMonth() / 3) + 1;
  const quarter = `Q${qNum}-${now.getFullYear()}`;

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/quarterly-bonus-summary?quarter=${quarter}`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      console.error('Failed:', e);
    } finally { setLoading(false); }
  };

  const handleConfirm = async (userId, teamName, amount, roleLabel, breakdown) => {
    setConfirming(`${userId}_${teamName}`);
    try {
      await axios.post(`${API_URL}/api/admin/quarterly-bonuses/confirm`, {
        user_id: userId, team_name: teamName, amount, quarter, role_label: roleLabel, breakdown,
      }, { withCredentials: true });
      fetchData();
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.detail || e.message));
    } finally { setConfirming(null); }
  };

  if (loading) return <div className="flex justify-center py-12"><SpinnerGap size={32} className="animate-spin text-blue-600" /></div>;
  if (!data) return <p className="text-center text-slate-400 py-8">Failed to load bonus data</p>;

  const eligibleTeams = data.teams.filter(t => t.eligible);
  const ineligibleTeams = data.teams.filter(t => !t.eligible);

  const MultiplierBadge = ({ mult }) => {
    if (mult === 0) return <Badge className="bg-red-100 text-red-700">0x</Badge>;
    if (mult < 1) return <Badge className="bg-amber-100 text-amber-700">{mult}x</Badge>;
    if (mult === 1) return <Badge className="bg-blue-100 text-blue-700">1x</Badge>;
    return <Badge className="bg-emerald-100 text-emerald-700">{mult}x</Badge>;
  };

  const BonusRow = ({ info, teamName, isAdmin: admin }) => {
    if (!info) return null;
    const isConfirmed = info.status === 'confirmed';
    const breakdown = `${info.he_count}HE×${info.multiplier}×${fINR(info.he_amount)} + ${info.le_count}LE×${info.multiplier}×${fINR(info.le_amount)}`;
    return (
      <div className={`p-3 rounded-lg border ${isConfirmed ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200'}`} data-testid={`bonus-row-${info.user_id}`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-slate-800">{info.user_name} <span className="text-xs text-slate-400">({info.role})</span></p>
            <div className="flex gap-3 mt-1 text-[10px] text-slate-500">
              <span>Eng: {info.he_count} × {info.multiplier} × {fINR(info.he_amount)} = {fINR(info.he_bonus)}</span>
              <span>LE: {info.le_count} × {info.multiplier} × {fINR(info.le_amount)} = {fINR(info.le_bonus)}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <p className={`font-bold text-lg ${info.total_bonus > 0 ? 'text-emerald-700' : 'text-slate-400'}`}>{fINR(info.total_bonus)}</p>
            {admin && info.total_bonus > 0 && !isConfirmed && (
              <Button
                size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-xs h-7 px-2"
                onClick={() => handleConfirm(info.user_id, teamName, info.total_bonus, info.role, breakdown)}
                disabled={confirming === `${info.user_id}_${teamName}`}
                data-testid={`confirm-bonus-${info.user_id}`}
              >
                {confirming === `${info.user_id}_${teamName}` ? <SpinnerGap size={14} className="animate-spin mr-1" /> : <CheckCircle size={14} className="mr-1" />}
                Confirm
              </Button>
            )}
            {isConfirmed && <Badge className="bg-emerald-200 text-emerald-800 text-[10px]">CONFIRMED</Badge>}
          </div>
        </div>
      </div>
    );
  };

  const renderTeamCard = (team) => (
    <Card key={team.team_name} className={`border-slate-200 ${team.eligible ? '' : 'opacity-60'}`} data-testid={`qb-team-${team.team_name}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {team.eligible && <Trophy size={18} className="text-amber-500" />}
            <p className="font-semibold text-slate-900">{team.team_name}</p>
            <Badge className={team.department === 'sales' ? 'bg-blue-100 text-blue-700 text-[10px]' : 'bg-teal-100 text-teal-700 text-[10px]'}>{team.department}</Badge>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <div className="text-center">
              <p className="text-[9px] text-slate-400 uppercase">Achieved</p>
              <p className="font-bold">{team.achieved}/{team.quarterly_target}</p>
            </div>
            <div className="text-center">
              <p className="text-[9px] text-slate-400 uppercase">%</p>
              <p className={`font-bold ${team.achievement_pct >= 100 ? 'text-emerald-700' : team.achievement_pct >= 90 ? 'text-blue-700' : 'text-slate-500'}`}>{team.achievement_pct}%</p>
            </div>
            <div className="text-center">
              <p className="text-[9px] text-slate-400 uppercase">Multiplier</p>
              <MultiplierBadge mult={team.multiplier} />
            </div>
          </div>
        </div>
        {/* Progress bar */}
        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all ${team.achievement_pct >= 100 ? 'bg-emerald-500' : team.achievement_pct >= 90 ? 'bg-blue-500' : team.achievement_pct >= 50 ? 'bg-amber-500' : 'bg-red-400'}`}
            style={{ width: `${Math.min(team.achievement_pct, 100)}%` }} />
        </div>
        {/* Bonus rows */}
        {team.eligible ? (
          <div className="space-y-2">
            <BonusRow info={team.lead} teamName={team.team_name} isAdmin={isAdmin} />
            <BonusRow info={team.manager} teamName={team.team_name} isAdmin={isAdmin} />
          </div>
        ) : (
          <p className="text-xs text-red-500 text-center py-1">Below 90% — No bonus eligible</p>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6" data-testid="quarterly-bonus-summary">
      <div>
        <h2 className="text-xl font-bold text-slate-900 font-['Outfit']">Quarterly Bonus Summary — {quarter}</h2>
        <p className="text-sm text-slate-500 mt-1">
          Formula: Targets Achieved × Count Multiplier × Per-Placement Amount. Only teams with ≥90% achievement qualify.
        </p>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-slate-200 bg-slate-900"><CardContent className="p-3">
          <p className="text-[10px] uppercase text-slate-400">Eligible Teams</p>
          <p className="text-2xl font-bold text-emerald-400">{eligibleTeams.length}<span className="text-sm text-slate-500">/{data.teams.length}</span></p>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-3">
          <p className="text-[10px] uppercase text-slate-400">Total Lead Bonus</p>
          <p className="text-xl font-bold text-blue-700">{fINR(eligibleTeams.reduce((s, t) => s + (t.lead?.total_bonus || 0), 0))}</p>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-3">
          <p className="text-[10px] uppercase text-slate-400">Total Manager Bonus</p>
          <p className="text-xl font-bold text-purple-700">{fINR(eligibleTeams.reduce((s, t) => s + (t.manager?.total_bonus || 0), 0))}</p>
        </CardContent></Card>
        <Card className="border-emerald-200 bg-emerald-50"><CardContent className="p-3">
          <p className="text-[10px] uppercase text-emerald-500">Grand Total</p>
          <p className="text-xl font-bold text-emerald-800">{fINR(eligibleTeams.reduce((s, t) => s + (t.lead?.total_bonus || 0) + (t.manager?.total_bonus || 0), 0))}</p>
        </CardContent></Card>
      </div>

      {/* Eligible Teams */}
      {eligibleTeams.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-emerald-700 uppercase flex items-center gap-1"><ArrowUp size={14} /> Eligible ({eligibleTeams.length})</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {eligibleTeams.map(renderTeamCard)}
          </div>
        </div>
      )}

      {/* Ineligible Teams */}
      {ineligibleTeams.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-slate-400 uppercase flex items-center gap-1"><ArrowDown size={14} /> Below Threshold ({ineligibleTeams.length})</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {ineligibleTeams.map(renderTeamCard)}
          </div>
        </div>
      )}
    </div>
  );
};

export default QuarterlyBonusSummary;
